/**
 * ChannelDetail.tsx — FR-015 / T316.
 *
 * The wired surface for the channel-health diagnostics orphan
 * (specs/exploration/stub_orphan_inventory.md §5 orphan #9):
 *
 *   - api.channelState.getChannelDiagnostics (read-only query,
 *     `requireAuth`; operator-only telemetry)
 *
 * The original detail page is re-exported from `_bundle.tsx` and the
 * "Diagnóstico" panel is appended with the live diagnostics rollup.
 * No mutations are wired here; the diagnostics query is the orphan.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { CapPill, Panel, Pill, Skeleton, StatCard } from '../../components/primitives';
import { ChannelDetail as ChannelDetailShell } from './_bundle';

interface ChannelDetailProps {
  id?: string;
}

interface Diagnostics {
  windowMs: number;
  stallCount: number;
  slowUploadCount: number;
  highGapCount: number;
  lastStallAt: number | null;
  lastSlowUploadAt: number | null;
  lastHighGapAt: number | null;
  totalIssues: number;
  isDegraded: boolean;
}

function ago(ts: number | null): string {
  if (!ts) return '—';
  const delta = Date.now() - ts;
  const m = Math.floor(delta / 60_000);
  if (m < 1) return 'hace segundos';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  return `hace ${h} h`;
}

export function ChannelDetail(props: ChannelDetailProps) {
  const diagnostics = useQuery(
    api.channelState.getChannelDiagnostics,
    props.id ? { channelId: props.id as any } : 'skip',
  ) as Diagnostics | undefined;

  return (
    <div>
      <ChannelDetailShell {...props} />
      <Panel
        title="Diagnóstico (última hora)"
        right={<CapPill>Telemetría en vivo</CapPill>}
        style={{ marginTop: 24 }}
      >
        {diagnostics === undefined ? (
          <Skeleton h={64} />
        ) : (
          <>
            <div className="circa-grid-4">
              <StatCard
                label="Bloqueos"
                value={diagnostics.stallCount}
                hint={ago(diagnostics.lastStallAt)}
                tone={diagnostics.stallCount > 0 ? 'warn' : undefined}
              />
              <StatCard
                label="Subidas lentas"
                value={diagnostics.slowUploadCount}
                hint={ago(diagnostics.lastSlowUploadAt)}
                tone={diagnostics.slowUploadCount > 0 ? 'warn' : undefined}
              />
              <StatCard
                label="Huecos altos"
                value={diagnostics.highGapCount}
                hint={ago(diagnostics.lastHighGapAt)}
                tone={diagnostics.highGapCount > 0 ? 'warn' : undefined}
              />
              <StatCard
                label="Total"
                value={diagnostics.totalIssues}
                tone={diagnostics.isDegraded ? 'bad' : 'good'}
              />
            </div>
            <div style={{ marginTop: 14 }}>
              <Pill tone={diagnostics.isDegraded ? 'bad' : 'good'}>
                {diagnostics.isDegraded ? 'Estado degradado' : 'Estado normal'}
              </Pill>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

export default ChannelDetail;
